import { useHqScreenEvent, trackHq } from './analytics';
import {
  HQ_DESCRIPTION,
  HQ_HERO,
  HQ_NAME,
  PAINS,
  PROFESSIONALS_LINK,
  STAGES,
  TOOLS,
  TOOL_GROUPS,
} from './config';
import { GUIDES } from './guides';
import { HqLayout } from './layout';
import { HqLink } from './Link';
import {
  HqGuideCard,
  HqHarborBand,
  HqKicker,
  HqPainChip,
  HqSectionHeading,
  HqStageCard,
  HqToolCard,
} from './ui';

const HOME_TOOL_GROUPS = TOOL_GROUPS.filter((group) => group.id !== 'check-in');

export function HomePage() {
  useHqScreenEvent('HQ Home Screen');

  return (
    <HqLayout
      title={`${HQ_NAME}: starting place for coparents`}
      description={HQ_DESCRIPTION}
      path='/'
      jsonLd={[
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: HQ_NAME,
          url: 'https://coparentinghq.com/',
          description: HQ_DESCRIPTION,
          publisher: {
            '@type': 'Organization',
            name: 'Harbor',
            url: 'https://harborcoparent.com',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: HQ_NAME,
          url: 'https://coparentinghq.com/',
          description: HQ_DESCRIPTION,
          parentOrganization: {
            '@type': 'Organization',
            name: 'Harbor',
            url: 'https://harborcoparent.com',
          },
        },
      ]}
    >
      <section className='max-w-3xl'>
        <HqKicker>Free tools & guides</HqKicker>
        <h1 className='mt-3 font-hq text-4xl font-medium leading-[1.15] tracking-tight text-hq-ink sm:text-5xl lg:text-[3.25rem]'>
          {HQ_HERO}
        </h1>
        <p className='mt-5 max-w-2xl text-lg leading-relaxed text-hq-muted'>
          Short guides and standalone tools for the first week, the first schedule, and the first
          expense fight. Harbor is optional if you later want a shared calendar or ledger.
        </p>
        <div className='mt-7 flex flex-wrap gap-3'>
          <a
            href='#start'
            className='inline-flex items-center rounded-xl bg-hq-sage px-4 py-2.5 text-sm font-semibold text-white hover:bg-hq-sage-hover'
          >
            Start with where you are
          </a>
          <HqLink
            to='/tools'
            className='inline-flex items-center rounded-xl border border-hq-border-strong bg-hq-paper px-4 py-2.5 text-sm font-semibold text-hq-ink hover:border-hq-sage'
          >
            Browse tools
          </HqLink>
        </div>
      </section>

      <section id='start' className='mt-16 scroll-mt-24'>
        <HqSectionHeading kicker='Start here' title='Where are you right now?' />
        <p className='mt-3 max-w-2xl text-hq-muted'>
          Pick the closest situation. Each path has two or three next steps: a guide, a tool, or
          Harbor. Not a single dump link.
        </p>
        <ul className='mt-8 grid gap-4 lg:grid-cols-2'>
          {STAGES.map((stage, index) => (
            <li key={stage.id} className={index === STAGES.length - 1 ? 'lg:col-span-2' : undefined}>
              <HqStageCard stage={stage} index={index} />
            </li>
          ))}
        </ul>
      </section>

      <section className='mt-16'>
        <HqSectionHeading kicker='Shortcuts' title='Or name the problem' />
        <ul className='mt-5 flex flex-wrap gap-2.5'>
          {PAINS.map((pain) => (
            <li key={pain.id}>
              <HqPainChip pain={pain} />
            </li>
          ))}
        </ul>
      </section>

      <section className='mt-16'>
        <HqSectionHeading
          kicker='Do something now'
          title='Free tools'
          action={
            <HqLink to='/tools' className='text-sm font-semibold text-hq-sage hover:underline'>
              All tools
            </HqLink>
          }
        />
        <div className='mt-8 space-y-10'>
          {HOME_TOOL_GROUPS.map((group) => {
            const tools = TOOLS.filter((tool) => tool.category === group.id && tool.homePreview);
            if (tools.length === 0) return null;
            return (
              <div key={group.id}>
                <h3 className='text-sm font-semibold uppercase tracking-[0.14em] text-hq-legal'>
                  {group.label}
                </h3>
                <ul className='mt-3 grid gap-4 sm:grid-cols-2'>
                  {tools.map((tool) => (
                    <li key={tool.id}>
                      <HqToolCard tool={tool} location='home' />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className='mt-16'>
        <HqSectionHeading
          kicker='Read first'
          title='Guides you can finish today'
          action={
            <HqLink to='/guides' className='text-sm font-semibold text-hq-sage hover:underline'>
              All guides
            </HqLink>
          }
        />
        <ul className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {GUIDES.map((guide) => (
            <li key={guide.slug}>
              <HqGuideCard
                href={`/guides/${guide.slug}`}
                title={guide.title}
                outcome={guide.outcome}
                readMinutes={guide.readMinutes}
              />
            </li>
          ))}
        </ul>
      </section>

      <section className='mt-16'>
        <HqHarborBand location='home-band' />
      </section>

      <p className='mt-12 text-sm text-hq-muted'>
        <a
          href={PROFESSIONALS_LINK.href}
          className='font-medium text-hq-sage hover:underline'
          onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'professionals' })}
        >
          {PROFESSIONALS_LINK.label}
        </a>
      </p>
    </HqLayout>
  );
}
